/**
 * SearchBar Component
 *
 * Enhanced reusable search input component with modern UX.
 * Used across multiple pages for filtering and searching content.
 *
 * @component
 * @example
 * ```tsx
 * const [searchQuery, setSearchQuery] = useState('');
 *
 * <SearchBar
 *   searchQuery={searchQuery}
 *   onSearchChange={setSearchQuery}
 *   onClearSearch={() => setSearchQuery('')}
 *   autoFocus={true}
 * />
 * ```
 *
 * Features:
 * - Animated search icon that pulses on focus
 * - Clear button with smooth fade in/out animation
 * - Enhanced focus states with glow effect
 * - Optional auto-focus on mount
 * - Customizable placeholder text
 * - Keyboard shortcuts (Escape to clear)
 * - Responsive width (full width on mobile, auto on desktop)
 */

import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';

interface SearchBarProps {
  /** Current search query value */
  searchQuery: string;
  /** Callback when search query changes */
  onSearchChange: (query: string) => void;
  /** Callback to clear the search query */
  onClearSearch: () => void;
  /** Whether to auto-focus the input on mount (default: false) */
  autoFocus?: boolean;
  /** Placeholder text for the input (default: "Search all categories...") */
  placeholder?: string;
}

export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(({
  searchQuery,
  onSearchChange,
  onClearSearch,
  autoFocus = false,
  placeholder = 'Search all categories...',
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Expose the input ref to parent components
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape to clear search
      if (e.key === 'Escape' && searchQuery) {
        onClearSearch();
        inputRef.current?.blur();
      }
      // Cmd/Ctrl + K to focus search (common pattern)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchQuery, onClearSearch]);

  return (
    <div className="flex items-center gap-4 w-full md:w-auto">
      {/* Enhanced Search Input */}
      <div className="relative flex-grow group">
        {/* Search Icon with animation */}
        <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-all duration-200 ${
          isFocused ? 'text-primary scale-110' : 'text-ink-faint'
        }`}>
          <i className="fa-solid fa-search text-lg"></i>
        </div>

        {/* Input Field */}
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className={`w-full px-4 py-3 pl-12 pr-12 bg-surface border-2 rounded-xl font-medium text-ink-body placeholder-ink-faint transition-all duration-200 outline-none ${
            isFocused
              ? 'border-cyan-500 shadow-lg shadow-cyan-500/20 ring-4 ring-hue-cyan-border-subtle'
              : 'border-line-strong hover:border-line-hover shadow-sm'
          }`}
          autoFocus={autoFocus}
        />

        {/* Clear Button */}
        {searchQuery && (
          <button
            onClick={onClearSearch}
            className="absolute inset-y-0 right-0 pr-4 flex items-center text-ink-faint hover:text-danger-mid cursor-pointer transition-colors duration-200"
            title="Clear search (Esc)"
          >
            <i className="fa-solid fa-times-circle text-xl"></i>
          </button>
        )}
      </div>
    </div>
  );
});

SearchBar.displayName = 'SearchBar';
